/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'

export type CustomerImportOptions = {
  delimiter?: string
  updateIfExists?: boolean
  columns?: {
    name?: string
    contact?: string
    phone?: string
    email?: string
  }
}

export type CustomerImportRow = {
  line: number
  raw: Record<string, any>
  data?: { name: string; phone?: string | null; email?: string | null }
  status: 'OK' | 'KO'
  errors?: Array<{ field?: string; message: string }>
  action?: 'CREATE' | 'UPDATE' | 'SKIP'
  match?: { by: 'email' | 'phone'; id: string } | null
}

export type CustomerImportPreview = {
  summary: {
    total: number
    valid: number
    invalid: number
    willCreate: number
    willUpdate: number
    willSkip: number
  }
  rows: CustomerImportRow[]
}

export type CustomerImportCommitResult = {
  ok: true
  summary: CustomerImportPreview['summary']
  created: number
  updated: number
  skipped: number
  createdIds: string[]
  updatedIds: string[]
}

function buildCustomerImportFormData(
  file: File,
  options: CustomerImportOptions
) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('delimiter', options.delimiter ?? ',')
  fd.append('updateIfExists', String(options.updateIfExists ?? true))
  if (options.columns) fd.append('columns', JSON.stringify(options.columns))
  return fd
}

export const usePreviewCustomersImportMutation = () => {
  return useMutation({
    mutationFn: async (vars: {
      file: File
      options: CustomerImportOptions
    }) => {
      const fd = buildCustomerImportFormData(vars.file, vars.options)
      const { data } = await apiClient.post<CustomerImportPreview>(
        '/customers/_import/preview',
        fd
        // ⚠️ ne mets PAS Content-Type: multipart/form-data manuellement
      )
      return data
    },
  })
}

export const useCommitCustomersImportMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      file: File
      options: CustomerImportOptions
    }) => {
      const fd = buildCustomerImportFormData(vars.file, vars.options)
      const { data } = await apiClient.post<CustomerImportCommitResult>(
        '/customers/_import/commit',
        fd
      )
      return data
    },
    onSuccess: () => {
      // refresh liste clients après import
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}
